import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import Skeleton from "@mui/material/Skeleton";
import PublicIcon from "@mui/icons-material/Public";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { enGB } from "date-fns/locale/en-GB";
import { format } from "date-fns";
import { useTranslation } from "../../hooks";
import { ConsultationMode } from "./types";
import { getTimezoneOptions, formatDateToISO, calculateDuration, } from "./utils/date";
import { resolveSlotDisplayTimes, formatTimeRange, getSlotHourInTimezone, isSlotInPast, } from "../../utils/slot";
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTH_NAMES = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];
export function SchedulePanel({ state, dispatch, isMobile = false, compactLayout = false, onRetrySlots, slotsHeadingLabel, slotsLoadingLabel, slotsEmptyLabel, showDuration = true, consultationMode, availableModes, onConsultationModeChange, availableDurations = [], selectedDuration = null, onDurationChange, }) {
    const { t } = useTranslation("appointment");
    const { t: tCommon } = useTranslation("common");
    const { selectedDoctor } = state;
    const showMode = !!consultationMode;
    const modeControl = (variant) => {
        if (!consultationMode)
            return null;
        if (onConsultationModeChange &&
            availableModes &&
            availableModes.length > 1) {
            return (_jsx(ConsultationModeSwitch, { mode: consultationMode, availableModes: availableModes, onChange: onConsultationModeChange, variant: variant, isMobile: isMobile, t: t }));
        }
        return (_jsx(ConsultationModeLabel, { mode: consultationMode, variant: variant, t: t }));
    };
    const duration = useMemo(() => {
        if (state.slots.length === 0)
            return null;
        return calculateDuration(state.slots[0].start, state.slots[0].end, 15);
    }, [state.slots]);
    if (!selectedDoctor) {
        return (_jsxs(Stack, { sx: { flex: 1, minHeight: 0 }, children: [showMode && (_jsx(Stack, { direction: "row", justifyContent: "center", sx: { pt: isMobile ? 1.5 : 2, px: 2 }, children: modeControl("plain") })), _jsxs(Stack, { alignItems: "center", justifyContent: "center", sx: { flex: 1, color: "text.secondary" }, children: [_jsx(Typography, { sx: { fontSize: 28, mb: 1, opacity: 0.4 }, children: "\ud83d\udccb" }), _jsx(Typography, { variant: "body2", sx: { fontWeight: 600, color: "text.primary" }, children: t("selectDoctorPrompt") }), _jsx(Typography, { variant: "caption", color: "text.secondary", children: t("chooseFromList") })] })] }));
    }
    return (_jsxs(Box, { className: "medos-booking-widget", sx: {
            flex: 1,
            minHeight: 0,
            bgcolor: "background.paper",
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
        }, children: [_jsx(Box, { sx: {
                    px: compactLayout ? 2 : 2.5,
                    py: compactLayout ? 1.5 : 1.85,
                    bgcolor: "secondary.main",
                    color: "secondary.contrastText",
                    flexShrink: 0,
                }, children: _jsxs(Stack, { direction: "row", spacing: 1.25, alignItems: "center", justifyContent: "space-between", children: [_jsxs(Stack, { direction: "row", spacing: 1.5, alignItems: "center", sx: { minWidth: 0 }, children: [_jsx(Box, { sx: {
                                        width: 44,
                                        height: 44,
                                        borderRadius: 1.75,
                                        bgcolor: "rgba(255,255,255,0.14)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                    }, children: _jsx(CalendarMonthOutlinedIcon, { sx: { fontSize: 24 } }) }), _jsxs(Box, { sx: { minWidth: 0 }, children: [_jsx(Typography, { sx: {
                                                fontWeight: 700,
                                                fontSize: 18,
                                                color: "inherit",
                                                lineHeight: 1.25,
                                            }, children: t("selectDateTime") }), _jsx(Typography, { sx: {
                                                fontSize: 13,
                                                color: "rgba(255,255,255,0.8)",
                                                lineHeight: 1.3,
                                            }, children: t("choosePreferredSlot") })] })] }), modeControl("onColor")] }) }), _jsx(CalendarSide, { state: state, dispatch: dispatch, compact: isMobile, dense: compactLayout, duration: duration, showDuration: showDuration, availableDurations: availableDurations, selectedDuration: selectedDuration, onDurationChange: onDurationChange, t: t }), _jsx(Divider, {}), _jsx(SlotSide, { state: state, dispatch: dispatch, columns: 3, dense: compactLayout, onRetry: onRetrySlots, headingLabel: slotsHeadingLabel, loadingLabel: slotsLoadingLabel, emptyLabel: slotsEmptyLabel, t: t, tCommon: tCommon })] }));
}
function DurationSelector({ durations, selectedDuration, onChange, isMobile, t, }) {
    return (_jsxs(Stack, { direction: isMobile ? "column" : "row", spacing: 0.75, alignItems: isMobile ? "stretch" : "center", sx: {
            flexShrink: 0,
            minWidth: isMobile ? 0 : "fit-content",
        }, children: [_jsxs(Stack, { direction: "row", spacing: 0.5, alignItems: "center", sx: { color: "text.secondary" }, children: [_jsx(AccessTimeFilledIcon, { sx: { fontSize: 14 } }), _jsx(Typography, { component: "span", sx: { fontSize: 11.5, fontWeight: 700, lineHeight: 1 }, children: t("duration", "Duration") })] }), _jsx(Stack, { direction: "row", role: durations.length > 2 ? undefined : "radiogroup", "aria-label": durations.length > 2 ? undefined : t("duration", "Duration"), sx: {
                    width: isMobile ? "100%" : "auto",
                    p: "2px",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1.5,
                    bgcolor: "background.paper",
                }, children: durations.length > 2 ? (_jsx(TextField, { select: true, size: "small", value: selectedDuration ?? "", onChange: (event) => onChange(Number(event.target.value)), slotProps: {
                        select: {
                            "aria-label": t("duration", "Duration"),
                        },
                    }, sx: {
                        minWidth: isMobile ? 0 : 112,
                        width: isMobile ? "100%" : "auto",
                        "& .MuiOutlinedInput-root": {
                            height: 33,
                            border: 0,
                            borderRadius: 1,
                        },
                        "& .MuiOutlinedInput-notchedOutline": { border: 0 },
                        "& .MuiSelect-select": {
                            py: 0.5,
                            px: 1,
                            fontSize: 12,
                            fontWeight: 600,
                            color: "text.primary",
                        },
                    }, children: durations.map((duration) => (_jsx(MenuItem, { value: duration, sx: { fontSize: 12 }, children: t("minutes", "{{count}} min", { count: duration }) }, duration))) })) : (durations.map((duration) => {
                    const selected = selectedDuration === duration;
                    return (_jsx(Button, { type: "button", size: "small", role: "radio", "aria-checked": selected, onClick: () => onChange(duration), sx: {
                            flex: isMobile ? 1 : "initial",
                            minWidth: isMobile ? 0 : 68,
                            height: 29,
                            px: 1.25,
                            borderRadius: 1,
                            color: selected ? "success.dark" : "text.secondary",
                            bgcolor: selected ? "success.50" : "transparent",
                            fontSize: 12,
                            fontWeight: selected ? 700 : 500,
                            lineHeight: 1,
                            textTransform: "none",
                            boxShadow: selected
                                ? "inset 0 0 0 1px rgba(46, 125, 50, 0.35)"
                                : "none",
                            "&:hover": {
                                bgcolor: selected ? "success.50" : "action.hover",
                            },
                            "&:focus-visible": {
                                outline: "2px solid",
                                outlineColor: "success.main",
                                outlineOffset: -1,
                            },
                        }, children: t("minutes", "{{count}} min", { count: duration }) }, duration));
                })) })] }));
}
function DurationLoadingSkeleton({ isMobile, }) {
    return (_jsxs(Stack, { direction: "row", spacing: 0.75, alignItems: "center", sx: {
            flexShrink: 0,
            width: isMobile ? "100%" : 126,
            justifyContent: isMobile ? "space-between" : "flex-start",
        }, role: "status", "aria-label": "Loading duration options", children: [_jsx(Skeleton, { variant: "text", width: 58, height: 20 }), _jsx(Skeleton, { variant: "rounded", width: isMobile ? 104 : 64, height: 33, sx: { borderRadius: 1.5 } })] }));
}
function ConsultationModeSwitch({ mode, availableModes, onChange, variant, isMobile, t, }) {
    const onColor = variant === "onColor";
    const renderBtn = (value, label, icon) => {
        const active = mode === value;
        return (_jsxs(Box, { component: "button", type: "button", role: "radio", "aria-checked": active, "aria-label": label, onClick: () => onChange(value), sx: {
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                py: "5px",
                px: isMobile ? 1 : 1.25,
                fontSize: 12,
                fontWeight: active ? 700 : 500,
                lineHeight: 1,
                border: "none",
                cursor: "pointer",
                borderRadius: "50px",
                whiteSpace: "nowrap",
                transition: "all 0.15s",
                ...(onColor
                    ? {
                        bgcolor: active ? "#fff" : "transparent",
                        color: active ? "secondary.main" : "rgba(255,255,255,0.85)",
                    }
                    : {
                        bgcolor: active ? "secondary.main" : "transparent",
                        color: active ? "secondary.contrastText" : "text.secondary",
                    }),
                "&:focus-visible": {
                    outline: "2px solid",
                    outlineColor: onColor ? "#fff" : "secondary.main",
                    outlineOffset: 1,
                },
            }, children: [icon, !isMobile && _jsx("span", { children: label })] }));
    };
    return (_jsxs(Box, { role: "radiogroup", "aria-label": t("consultationMode", "Consultation mode"), sx: {
            display: "flex",
            flexShrink: 0,
            gap: "2px",
            p: "3px",
            borderRadius: "50px",
            ...(onColor
                ? { bgcolor: "rgba(255,255,255,0.15)" }
                : { border: 1, borderColor: "divider", bgcolor: "background.paper" }),
        }, children: [availableModes.includes(ConsultationMode.OFFLINE) &&
                renderBtn(ConsultationMode.OFFLINE, t("inPerson", "In-Person"), _jsx(StorefrontOutlinedIcon, { sx: { fontSize: 15 } })), availableModes.includes(ConsultationMode.ONLINE) &&
                renderBtn(ConsultationMode.ONLINE, t("online", "Online"), _jsx(VideocamOutlinedIcon, { sx: { fontSize: 16 } }))] }));
}
function ConsultationModeLabel({ mode, variant, t, }) {
    const onColor = variant === "onColor";
    const online = mode === ConsultationMode.ONLINE;
    return (_jsxs(Stack, { direction: "row", alignItems: "center", spacing: 0.5, sx: {
            flexShrink: 0,
            borderRadius: "50px",
            px: 1.25,
            py: "5px",
            ...(onColor
                ? { bgcolor: "rgba(255,255,255,0.15)", color: "#fff" }
                : {
                    border: 1,
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    color: "text.secondary",
                }),
        }, children: [online ? (_jsx(VideocamOutlinedIcon, { sx: { fontSize: 15 } })) : (_jsx(StorefrontOutlinedIcon, { sx: { fontSize: 15 } })), _jsx(Typography, { sx: { fontSize: 12.5, fontWeight: 600, lineHeight: 1.2 }, children: online ? t("online", "Online") : t("inPerson", "In-Person") })] }));
}
function CalendarSide({ state, dispatch, compact = false, dense = false, duration, showDuration = true, availableDurations = [], selectedDuration = null, onDurationChange, t, }) {
    const { selectedDate, selectedTimezone } = state;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return (_jsxs(Box, { sx: {
            px: dense ? 1.35 : compact ? 1.5 : 2.25,
            pt: dense ? 1.2 : compact ? 1.5 : 2,
            pb: dense ? 1.25 : 2,
        }, children: [_jsx(LocalizationProvider, { dateAdapter: AdapterDateFns, adapterLocale: enGB, children: _jsx(DateCalendar, { reduceAnimations: true, disablePast: true, value: selectedDate, dayOfWeekFormatter: (date) => format(date, "EEE", { locale: enGB }).toUpperCase(), onChange: (nextDate) => {
                        if (nextDate) {
                            const normalizedDate = new Date(nextDate);
                            normalizedDate.setHours(0, 0, 0, 0);
                            dispatch({ type: "SET_SELECTED_DATE", payload: normalizedDate });
                        }
                    }, sx: {
                        width: "100%",
                        maxWidth: "100%",
                        maxHeight: "none",
                        m: 0,
                        "& .MuiPickersCalendarHeader-root": {
                            px: 0,
                            mb: dense ? 0.75 : 1.25,
                        },
                        "& .MuiPickersCalendarHeader-label": {
                            fontSize: dense ? 18 : 22,
                            fontWeight: 700,
                            color: "text.primary",
                            letterSpacing: 0,
                        },
                        "& .MuiDayCalendar-weekDayLabel": {
                            color: "text.secondary",
                            fontSize: 11,
                            fontWeight: 700,
                            textTransform: "uppercase",
                        },
                        "& .MuiDayCalendar-header": {
                            justifyContent: "space-between",
                            mx: 0,
                        },
                        "& .MuiDayCalendar-weekContainer": {
                            justifyContent: "space-between",
                            mx: 0,
                            mt: dense ? 0.65 : 0.85,
                        },
                        "& .MuiPickersDay-root": {
                            width: dense ? 36 : compact ? 38 : 42,
                            height: dense ? 34 : compact ? 36 : 40,
                            borderRadius: 1.5,
                            fontWeight: 600,
                            fontSize: dense ? 12.5 : 13,
                            color: "text.primary",
                            bgcolor: "#F3F4F6",
                            "&:hover": { bgcolor: "#E5E7EB" },
                        },
                        "& .MuiPickersDay-root.Mui-disabled": {
                            bgcolor: "transparent",
                            color: "text.disabled",
                        },
                        "& .MuiPickersDay-root.MuiPickersDay-dayOutsideMonth": {
                            bgcolor: "transparent",
                        },
                        "& .MuiPickersDay-root.Mui-selected": {
                            bgcolor: "secondary.main",
                            color: "secondary.contrastText",
                            boxShadow: "0 8px 18px rgba(15, 23, 42, 0.18)",
                            "&:hover": { bgcolor: "secondary.main" },
                        },
                        "& .MuiPickersDay-today": {
                            border: "1px solid",
                            borderColor: "primary.main",
                        },
                        "& .MuiPickersArrowSwitcher-root button": {
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1.25,
                        },
                        "& .MuiDayCalendar-slideTransition": {
                            minHeight: dense ? 210 : 232,
                        },
                        "& .MuiDayCalendar-monthContainer": {
                            position: "relative",
                        },
                    } }) }), _jsxs(Stack, { direction: compact ? "column" : "row", spacing: 1, alignItems: compact ? "stretch" : "center", sx: { mt: dense ? 0.75 : 1.25 }, children: [_jsx(TextField, { select: true, size: "small", fullWidth: true, value: selectedTimezone, onChange: (e) => dispatch({ type: "SET_TIMEZONE", payload: e.target.value }), slotProps: {
                            select: { "aria-label": t("timezone") },
                            input: {
                                startAdornment: (_jsx(PublicIcon, { sx: { fontSize: 14, color: "text.secondary", mr: 0.75 } })),
                            },
                        }, sx: {
                            flex: 1,
                            "& .MuiInputBase-input": { fontSize: 11.5 },
                            "& .MuiInputBase-root": { height: dense ? 34 : undefined },
                        }, children: getTimezoneOptions(selectedTimezone).map((tz) => (_jsx(MenuItem, { value: tz.value, sx: { fontSize: 12 }, children: tz.label }, tz.value))) }), showDuration &&
                        availableDurations.length === 0 &&
                        onDurationChange &&
                        state.slotsLoading ? (_jsx(DurationLoadingSkeleton, { isMobile: compact })) : showDuration &&
                        availableDurations.length > 0 &&
                        onDurationChange ? (_jsx(DurationSelector, { durations: availableDurations, selectedDuration: selectedDuration, onChange: onDurationChange, isMobile: compact, t: t })) : showDuration ? (_jsx(Chip, { icon: _jsx(AccessTimeFilledIcon, { sx: { fontSize: "14px !important" } }), label: duration
                            ? t("durationMins", undefined, { count: duration })
                            : t("defaultDurationMins"), variant: "outlined", sx: {
                            height: dense ? 34 : 40,
                            justifyContent: "flex-start",
                            px: 0.75,
                        } })) : null] })] }));
}
function SlotSide({ state, dispatch, columns = 2, dense = false, onRetry, headingLabel, loadingLabel, emptyLabel, t, tCommon, }) {
    const { slots, selectedSlot, selectedDate, selectedTimezone, slotsLoading, slotsError, selectedDoctor, } = state;
    const dateStr = formatDateToISO(selectedDate);
    const todayStr = formatDateToISO(new Date());
    const isToday = dateStr === todayStr;
    const groups = useMemo(() => {
        const morning = [];
        const afternoon = [];
        const evening = [];
        for (const slot of slots) {
            if (isToday && isSlotInPast(slot, selectedDate))
                continue;
            const hour = getSlotHourInTimezone(slot, selectedTimezone, selectedDate);
            if (hour < 12)
                morning.push(slot);
            else if (hour < 17)
                afternoon.push(slot);
            else
                evening.push(slot);
        }
        const result = [];
        if (morning.length > 0)
            result.push({ label: t("morning"), slots: morning });
        if (afternoon.length > 0)
            result.push({ label: t("afternoon"), slots: afternoon });
        if (evening.length > 0)
            result.push({ label: t("evening"), slots: evening });
        return result;
    }, [slots, selectedDate, selectedTimezone, isToday, t]);
    const hasSlots = groups.some((g) => g.slots.length > 0);
    if (!selectedDoctor) {
        return _jsx(Box, { sx: { p: 2.5, minHeight: 0 } });
    }
    return (_jsxs(Box, { sx: {
            p: dense ? "12px 14px 14px" : "16px 18px 18px",
            overflowY: "auto",
            overflowX: "hidden",
            display: "flex",
            minHeight: 0,
            maxHeight: dense ? 240 : 300,
            flexDirection: "column",
            flex: 1,
        }, children: [_jsx(Typography, { variant: "subtitle1", sx: { fontWeight: 700, mb: dense ? 1 : 1.5, lineHeight: 1.2 }, children: headingLabel ?? t("availableSlots") }), slotsLoading && (_jsx(SlotSkeletonGrid, { columns: columns, dense: dense, label: loadingLabel ?? t("loadingAvailableSlots") })), !slotsLoading && (slotsError || !hasSlots) && (_jsx(Stack, { alignItems: "center", justifyContent: "center", sx: { flex: 1, py: dense ? 2.5 : 4 }, children: slotsError ? (_jsxs(Stack, { alignItems: "center", spacing: 1.5, children: [_jsx(Box, { sx: {
                                width: 48,
                                height: 48,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "error.light",
                                color: "error.main",
                            }, children: _jsx(ErrorOutlineIcon, { sx: { fontSize: 26 } }) }), _jsx(Typography, { variant: "body2", sx: {
                                fontWeight: 600,
                                color: "text.primary",
                                textAlign: "center",
                            }, children: t("couldntLoadSlots") }), _jsx(Typography, { variant: "caption", color: "text.secondary", sx: { textAlign: "center", maxWidth: 220, lineHeight: 1.5 }, children: slotsError }), onRetry && (_jsx(Button, { size: "small", variant: "outlined", startIcon: _jsx(RefreshIcon, { sx: { fontSize: 15 } }), onClick: onRetry, sx: { mt: 0.5, textTransform: "none", fontWeight: 600 }, children: tCommon("tryAgain") }))] })) : (_jsxs(Stack, { alignItems: "center", spacing: 1, children: [_jsx(Box, { sx: {
                                width: 48,
                                height: 48,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                bgcolor: "action.hover",
                                color: "text.secondary",
                            }, children: _jsx(CalendarMonthOutlinedIcon, { sx: { fontSize: 24 } }) }), _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { textAlign: "center", lineHeight: 1.5 }, children: emptyLabel ?? t("noSlotsAvailable") })] })) })), groups.map((group) => (_jsxs(Box, { sx: { mb: dense ? 0.85 : 1.25 }, children: [_jsx(Typography, { variant: "overline", sx: {
                            color: "primary.main",
                            letterSpacing: 0.7,
                            fontSize: 10,
                            display: "block",
                            mb: dense ? 0.5 : 0.75,
                        }, children: group.label }), _jsx(Box, { sx: {
                            display: "grid",
                            gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                            gap: dense ? 0.5 : 0.625,
                        }, children: group.slots.map((slot) => {
                            const times = resolveSlotDisplayTimes(slot, selectedTimezone, selectedDate);
                            const isSelected = selectedSlot?.start === slot.start;
                            const patientLabel = formatTimeRange(times.patientStart, times.patientEnd);
                            const clinicLabel = formatTimeRange(times.clinicStart, times.clinicEnd);
                            const dayMarker = times.dayOffset > 0 ? "+1" : times.dayOffset < 0 ? "-1" : null;
                            return (_jsxs(Button, { variant: "text", size: "small", disableElevation: true, onClick: () => dispatch({ type: "SET_SELECTED_SLOT", payload: slot }), "aria-pressed": isSelected, "aria-label": times.showClinicTime
                                    ? `${patientLabel} ${t("yourTimeSuffix", "your time")}, ${clinicLabel} ${t("clinicTimeSuffix", "clinic")}`
                                    : patientLabel, title: times.showClinicTime
                                    ? `${patientLabel} (${times.patientTimezone}) · ${clinicLabel} (${times.clinicTimezone})`
                                    : undefined, sx: {
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 0,
                                    overflow: "hidden",
                                    fontSize: dense ? 10 : 10.5,
                                    fontWeight: 600,
                                    textTransform: "none",
                                    fontVariantNumeric: "tabular-nums",
                                    py: dense ? 0.55 : 0.75,
                                    px: 0.4,
                                    minWidth: 0,
                                    lineHeight: 1.25,
                                    borderRadius: 1.25,
                                    border: 1,
                                    borderColor: isSelected ? "secondary.main" : "transparent",
                                    color: isSelected
                                        ? "secondary.contrastText"
                                        : "text.primary",
                                    bgcolor: isSelected ? "secondary.main" : "action.hover",
                                    boxShadow: isSelected
                                        ? "0 6px 14px rgba(15, 23, 42, 0.16)"
                                        : "none",
                                    "&:hover": {
                                        borderColor: "secondary.main",
                                        bgcolor: isSelected
                                            ? "secondary.main"
                                            : "action.selected",
                                    },
                                }, children: [_jsxs(Box, { component: "span", sx: {
                                            lineHeight: 1.25,
                                            whiteSpace: "nowrap",
                                            maxWidth: "100%",
                                        }, children: [patientLabel, dayMarker && (_jsx(Box, { component: "span", sx: {
                                                    ml: 0.25,
                                                    fontSize: 8.5,
                                                    fontWeight: 700,
                                                    verticalAlign: "super",
                                                }, children: dayMarker }))] }), times.showClinicTime && (_jsxs(Box, { component: "span", sx: {
                                            fontSize: 8,
                                            fontWeight: 500,
                                            lineHeight: 1.35,
                                            letterSpacing: 0,
                                            whiteSpace: "nowrap",
                                            maxWidth: "100%",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            color: isSelected
                                                ? "secondary.contrastText"
                                                : "text.secondary",
                                            opacity: isSelected ? 0.72 : 1,
                                        }, children: [clinicLabel, " ", t("clinicTimeSuffix", "clinic")] }))] }, slot.start));
                        }) })] }, group.label)))] }));
}
function SlotSkeletonGrid({ columns, dense, label, }) {
    const gridSx = {
        display: "grid",
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        gap: dense ? 0.5 : 0.625,
    };
    return (_jsx(Box, { role: "status", "aria-label": label, sx: { flex: 1 }, children: [0, 1].map((group) => (_jsxs(Box, { sx: { mb: dense ? 0.85 : 1.25 }, children: [_jsx(Skeleton, { variant: "text", width: group === 0 ? 64 : 72, sx: { fontSize: 10, mb: dense ? 0.5 : 0.75 } }), _jsx(Box, { sx: gridSx, children: Array.from({ length: group === 0 ? 6 : 4 }).map((_, i) => (_jsx(Skeleton, { variant: "rounded", height: dense ? 28 : 32, sx: { borderRadius: 1 } }, i))) })] }, group))) }));
}
